"use client";

import { useEffect, useState } from "react";

/* Admin-only one-click "Hide" for a catalog product (wall art / template).
 *
 * Lives on PUBLIC, SEO-critical PDPs. It self-gates: on mount it asks
 * /api/admin/whoami and renders NOTHING unless the viewer is an admin — so
 * the page stays static and there is zero cost or visual change for buyers
 * and crawlers. Clicking flips the product to draft (vanishes from every
 * buyable surface). We don't refresh the page after hiding (the drafted
 * product would 404); instead we show an inline confirmation + Undo. */
export function AdminProductHideButton({ slug }: { slug: string }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetch("/api/admin/whoami")
      .then((r) => (r.ok ? r.json() : { admin: false }))
      .then((d: { admin?: boolean }) => {
        if (alive) setIsAdmin(Boolean(d.admin));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  if (!isAdmin) return null;

  const toggle = async (action: "hide" | "unhide") => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await fetch("/api/admin/products/hide", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug, action }),
      });
      const data = (await res.json()) as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) throw new Error(data.error || "failed");
      setHidden(action === "hide");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-5 rounded-lg border border-dashed p-3" style={{ borderColor: "rgba(255,80,80,0.4)" }}>
      <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.14em] text-white/65">
        Admin · QC
      </div>
      {hidden ? (
        <div className="flex items-center gap-3">
          <span className="text-[13px] text-white/70">
            Hidden — no longer buyable or listed.
          </span>
          <button
            type="button"
            data-interactive
            onClick={() => void toggle("unhide")}
            disabled={busy}
            className="rounded-lg px-3 py-1.5 text-[13px] font-medium transition-colors"
            style={{ background: "rgba(159,255,61,0.14)", border: "1px solid rgba(159,255,61,0.5)", color: "#bfff7a" }}
          >
            {busy ? "…" : "Undo"}
          </button>
        </div>
      ) : (
        <button
          type="button"
          data-interactive
          onClick={() => void toggle("hide")}
          disabled={busy}
          className="rounded-lg px-4 py-2 text-[13px] font-medium transition-colors"
          style={{ background: "rgba(255,80,80,0.14)", border: "1px solid rgba(255,80,80,0.5)", color: "#ff9a9a" }}
        >
          {busy ? "…" : "🚫 Hide (broken / wrong)"}
        </button>
      )}
      {err && <p className="mt-2 text-[12px]" style={{ color: "#ef4444" }}>{err}</p>}
    </div>
  );
}
